import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import _ from "lodash";

import { LegacyBuyerTableFilters } from "components/buyers/useLegacyFilterableBuyerTableState";
import { graphql } from "lib/generated/app-service-gql";
import {
  BuyerSignalStatsRequest_SortEnum_0 as BuyerSignalStatsSort,
  BuyerSignalStatsRequest_SortOrderEnum_0 as BuyerSignalStatsSortOrder,
  FrameworkOrganisationsQuery,
} from "lib/generated/app-service-gql/graphql";
import { PaginationState, SortState } from "lib/search/types";
import { SearchOrgRole } from "lib/types/graphQLEnums";
import { assert } from "lib/utils";
import { BuyerSignalStatsRequest, useBuyerSignalStats } from "../organisations/useBuyerSignalStats";
import { useOrganisations } from "../organisations/useOrganisations";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";
import { useDescribeFramework } from "./useDescribeFramework";

const frameworkOrganisationsQuery = graphql(`
  query frameworkOrganisations($request: FrameworkOrganisationsRequest!) {
    frameworkOrganisations(FrameworkOrganisationsRequest: $request) {
      organisations {
        id
        name
        roles
        callOffs {
          count
          lastActivity
          averageAmount {
            amount
            currency
          }
        }
      }
    }
  }
`);

/** An organisation on a framework, with its roles and its call-offs through the framework. */
export type FrameworkOrganisation = {
  id: string;
  name: string;
  roles: string[];
  callOffs?: FrameworkOrganisationCallOffs;
};

export type FrameworkOrganisationCallOffs = {
  count: number;
  lastActivity?: Date;
  averageAmount: { amount?: number; currency?: string };
};

type SearchFrameworkOrganisationsQueryOptions = Pick<
  UseQueryOptions<
    SearchFrameworkOrganisationsResult,
    unknown,
    SearchFrameworkOrganisationsResult,
    []
  >,
  "enabled"
>;

/**
 * Optimally all of this would be implemented in the backend, but the buyer search and
 * the framework organisation stats are separate queries joined here.
 *
 * There are some important design decisions we should think through
 * before adding this to the graphql schema,
 * so it's less impactful to perform the search on the client
 * for now and then later clean it up once we're happy with how to model
 * organisation (or framework organisation) search in GraphQL.
 */

const CLIENT_SIDE_SORT_FIELDS = ["frameworkCallOffs", "frameworkLastActivity", "frameworkLots"];

/** Legacy sort-field names (URL state) -> buyerSignalStats sort fields. */
const BUYER_SORT_FIELD_MAP: Record<string, BuyerSignalStatsSort> = {
  buyer_name: BuyerSignalStatsSort.BuyerName,
  country: BuyerSignalStatsSort.Country,
  town: BuyerSignalStatsSort.Town,
  region: BuyerSignalStatsSort.Region,
  total_record_count: BuyerSignalStatsSort.NoticeCount,
  signals: BuyerSignalStatsSort.Signals,
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Maps the legacy buyer-filter URL state onto the buyerSignalStats filters. The legacy `signals`
 * array mixes signal ids and category names, disambiguated by shape. Supplier and location keys
 * have no equivalent (the framework UI hides those sections) and are dropped.
 */
export function toBuyerSignalStatsFilters(
  filters: LegacyBuyerTableFilters,
): BuyerSignalStatsRequest["filters"] {
  const signalIds = filters.signals?.filter((signal) => UUID_PATTERN.test(signal));
  const signalCategories = filters.signals?.filter((signal) => !UUID_PATTERN.test(signal));

  return {
    buyerName: filters.buyerName || undefined,
    buyerIds: filters.buyerGuids?.length ? filters.buyerGuids : undefined,
    buyerListIds: filters.buyerLists?.length ? filters.buyerLists : undefined,
    signalIds: signalIds?.length ? signalIds : undefined,
    signalCategories: signalCategories?.length ? signalCategories : undefined,
    categories:
      filters.categories?.categoryIds?.length || filters.categories?.includeUncategorised
        ? {
            categoryIds: filters.categories.categoryIds,
            includeUncategorised: filters.categories.includeUncategorised,
          }
        : undefined,
    buyerTypes: filters.buyerTypes?.length ? filters.buyerTypes : undefined,
    hqCountry:
      filters.hqCountry?.countries?.length || filters.hqCountry?.regions?.length
        ? {
            countries: filters.hqCountry.countries,
            regions: filters.hqCountry.regions,
          }
        : undefined,
  };
}

export type SearchFrameworkOrganisationsResult = {
  organisations: FrameworkOrganisation[];
  totalCount: number;
};

/**
 * Fetches a framework's buyers based on supplied filters.
 *
 * Optimally there would be a single codepath for buyers & suppliers,
 * but the filters are not compatible and for now we have to use
 * two different search APIs.
 *
 * Currently this is implemented in the frontend because
 * the buyer search and the framework organisation stats
 * are separate queries.
 */
export const useSearchFrameworkBuyers = (
  frameworkId: string,
  filters: LegacyBuyerTableFilters,
  sortState: SortState,
  paginationState: PaginationState,
  options?: SearchFrameworkOrganisationsQueryOptions,
): UseQueryResult<SearchFrameworkOrganisationsResult> => {
  const clientSideSort = CLIENT_SIDE_SORT_FIELDS.includes(sortState.field);

  const { data: frameworkData } = useDescribeFramework(frameworkId);

  const frameworkBuyerGuids = frameworkData?.buyers?.map((b) => b.id);

  /**
   * Fetch all framework buyers matching the filters.
   * We use the specified sort unless it's a field
   * we can only sort by in the frontend - then we use the default,
   * so that in most cases we'll have the data already fetched!
   */
  const { data: filteredBuyers } = useBuyerSignalStats(
    {
      buyerIds: frameworkBuyerGuids ?? [],
      sort: clientSideSort
        ? BuyerSignalStatsSort.BuyerName
        : BUYER_SORT_FIELD_MAP[sortState.field] ?? BuyerSignalStatsSort.BuyerName,
      sortOrder:
        clientSideSort || sortState.order !== "DESC"
          ? BuyerSignalStatsSortOrder.Asc
          : BuyerSignalStatsSortOrder.Desc,
      offset: 0,
      filters: toBuyerSignalStatsFilters(filters),
    },
    {
      ...options,
      enabled:
        (options?.enabled ?? true) && !!frameworkData && (frameworkBuyerGuids?.length ?? 0) > 0,
    },
  );

  /**
   * Fetch all information about all framework orgs in "buyer" roles.
   * We do this once because the data is not affected by filters and is fairly
   * cheap to calculate. Then we can reuse it when filters/sorting/pagination changes.
   */
  const { data: frameworkOrganisations } = useSearchFrameworkOrganisations(
    frameworkId,
    frameworkBuyerGuids,
    options,
  );

  /**
   * Combine the filtered buyers with framework data
   * and perform sorting & pagination as needed.
   */
  return useQuery(
    ["framework_buyers", filteredBuyers, frameworkOrganisations, sortState, paginationState],
    () => {
      assert(frameworkOrganisations);
      assert(filteredBuyers);

      const filteredBuyerGuids = filteredBuyers.signalStats.map((result) => result.buyerGuid);

      let organisations = frameworkOrganisations.organisations;

      if (clientSideSort) {
        switch (sortState.field) {
          case "frameworkCallOffs":
            organisations = _.sortBy(organisations, [
              (org) => org.callOffs?.count || 0,
              (org) => org.callOffs?.averageAmount || 0,
            ]);
            break;

          case "frameworkLastActivity":
            {
              const OLDEST_DATE = new Date(1900, 1, 1);
              organisations = _.sortBy(
                organisations,
                (org) => org.callOffs?.lastActivity || OLDEST_DATE,
              );
            }
            break;

          default:
            throw new Error(`Unsupported sort field ${sortState.field}`);
        }

        if (sortState.order === "DESC") {
          organisations.reverse();
        }
      } else {
        const orgById = new Map(organisations.map((org) => [org.id, org]));
        organisations = filteredBuyerGuids.map((orgId) => {
          const org = orgById.get(orgId);
          assert(org);
          return org;
        });
      }

      const offset = (paginationState.current - 1) * paginationState.pageSize;
      const limit = paginationState.pageSize;
      organisations = organisations.slice(offset, offset + limit);

      return {
        organisations,
        totalCount: filteredBuyers.totalCount,
      };
    },
    {
      ...options,
      enabled: !!(filteredBuyers && frameworkOrganisations),
    },
  );
};

export type FrameworkSupplierSearchFilters = {
  text?: string;
  isSme?: boolean;
  guid?: string[];
  country?: string[];
};

export const useSearchFrameworkSuppliers = (
  frameworkId: string,
  filters: FrameworkSupplierSearchFilters,
  sortState: SortState,
  paginationState: PaginationState,
  options?: SearchFrameworkOrganisationsQueryOptions,
) => {
  const { data: frameworkData } = useDescribeFramework(frameworkId);

  const frameworkSupplierGuids = frameworkData?.suppliers?.map((b) => b.id);

  // Only the filtered total is needed here; the table rows come from the framework org search.
  const { data: filteredSuppliers } = useOrganisations(
    {
      ids: frameworkSupplierGuids ?? [],
      names: filters.text ? [filters.text] : undefined,
      isSme: filters.isSme,
      countryCodes: filters.country?.length ? filters.country : undefined,
      role: SearchOrgRole.Supplier,
      limit: 1,
      page: 1,
      include: { noticeCount: false, awardCount: false, aliases: false },
    },
    {
      enabled:
        options?.enabled === false
          ? false
          : !!frameworkData && (frameworkSupplierGuids?.length ?? 0) > 0,
    },
  );

  const { data: frameworkOrganisations } = useSearchFrameworkOrganisations(
    frameworkId,
    frameworkSupplierGuids,
    options,
  );

  return useQuery(
    ["frameworkSuppliers", filteredSuppliers, frameworkOrganisations, sortState, paginationState],
    () => {
      assert(frameworkOrganisations);
      assert(filteredSuppliers);

      return {
        organisations: frameworkOrganisations.organisations,
        totalCount: filteredSuppliers.searchOrganisations.total,
      };
    },
    {
      ...options,
      enabled: !!(filteredSuppliers && frameworkOrganisations),
    },
  );
};

const useSearchFrameworkOrganisations = (
  frameworkId: string,
  organisationIds: string[] | undefined,
  options?: SearchFrameworkOrganisationsQueryOptions,
) => {
  const request = { frameworkId, organisationIds: organisationIds ?? [] };
  const { data, ...rest } = useGraphQlQuery<FrameworkOrganisationsQuery>(
    ["framework_organisations", request],
    frameworkOrganisationsQuery,
    [{ request }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
      enabled: (options?.enabled ?? true) && !!organisationIds,
    },
  );
  return {
    data: data && {
      organisations: data.frameworkOrganisations.organisations.map(toFrameworkOrganisation),
    },
    ...rest,
  };
};

function toFrameworkOrganisation(
  organisation: FrameworkOrganisationsQuery["frameworkOrganisations"]["organisations"][number],
): FrameworkOrganisation {
  const { callOffs } = organisation;
  return {
    id: organisation.id,
    name: organisation.name,
    roles: organisation.roles,
    callOffs: callOffs
      ? {
          count: callOffs.count,
          lastActivity: callOffs.lastActivity ? new Date(callOffs.lastActivity) : undefined,
          averageAmount: {
            amount: callOffs.averageAmount.amount ?? undefined,
            currency: callOffs.averageAmount.currency ?? undefined,
          },
        }
      : undefined,
  };
}
